import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plane, Users, Package, LayoutGrid, Building2, ArrowRight, Sparkles, MapPin, Calendar, Award, ChevronRight, FileEdit } from 'lucide-react';
import { IMAGES, RESCUED_PRODUCTS, COMPANIES_OF_MONTH } from '../mock';
import api from '../api';
import { useLang } from '../i18n/LanguageContext';
import RfqDialog from './platform/RfqDialog';

const ICONS = { missions: Plane, partenaires: Users, rescapes: Package, marchandises: LayoutGrid, entreprises: Building2 };
const SERVICE_IDS = ['missions', 'partenaires', 'rescapes', 'marchandises', 'entreprises'];
const NUMBERS = { missions: '01', partenaires: '02', rescapes: '03', marchandises: '04', entreprises: '05' };
const ROUTES = { missions: '/missions', partenaires: '/partenaires', rescapes: '/rescapes', marchandises: '/marchandises', entreprises: '/entreprises' };

export default function IndustrialPlatform() {
  const { t } = useLang();
  const [active, setActive] = useState('missions');
  const [rfqOpen, setRfqOpen] = useState(false);
  const service = t(`platform.services.${active}`);
  const ActiveIcon = ICONS[active];

  return (
    <section id="plateforme" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-16">
          <span className="section-label mb-4">{t('platform.label')}</span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-brand-navy mt-4 leading-tight">
            {t('platform.title1')} <span className="yellow-underline">{t('platform.title2')}</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto mt-8 leading-relaxed">{t('platform.intro')}</p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {SERVICE_IDS.map((id) => {
            const Icon = ICONS[id];
            const isActive = active === id;
            return (
              <button key={id} onClick={() => setActive(id)}
                className={`flex items-center gap-2 px-5 py-3 rounded-full font-medium text-sm transition-all border ${
                  isActive ? 'bg-brand-navy text-white border-brand-navy shadow-lg' : 'bg-white text-brand-navy border-gray-200 hover:border-brand-yellow'
                }`}>
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{t(`platform.services.${id}.title`)}</span>
                <span className="sm:hidden">{NUMBERS[id]}</span>
              </button>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 fade-in" key={active}>
          <div className="grid lg:grid-cols-5">
            <div className="lg:col-span-2 relative bg-brand-navy p-10 lg:p-12 text-white overflow-hidden">
              <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full bg-brand-yellow/10 blur-3xl" />
              <div className="relative z-10">
                <div className="text-brand-yellow text-6xl font-black mb-4">{NUMBERS[active]}</div>
                <div className="w-14 h-14 rounded-lg bg-brand-yellow/15 border border-brand-yellow/40 flex items-center justify-center mb-6">
                  <ActiveIcon className="w-7 h-7 text-brand-yellow" />
                </div>
                <h3 className="text-3xl font-bold mb-4">{service.title}</h3>
                <p className="text-white/80 leading-relaxed">{service.desc}</p>
                <div className="mt-8 space-y-2">
                  {service.highlights.map((h, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <ChevronRight className="w-4 h-4 text-brand-yellow shrink-0" />
                      <span className="text-white/90">{h}</span>
                    </div>
                  ))}
                </div>
                <Link to={ROUTES[active]} className="inline-flex items-center gap-2 mt-8 text-brand-yellow font-semibold hover:gap-3 transition-all">
                  {t('common.learnMore')} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-3 p-10 lg:p-12">
              {active === 'missions' && <MissionsContent t={t} />}
              {active === 'partenaires' && <PartnersContent t={t} onOpenRfq={() => setRfqOpen(true)} />}
              {active === 'rescapes' && <RescuedContent t={t} />}
              {active === 'marchandises' && <MerchandiseContent t={t} />}
              {active === 'entreprises' && <CompaniesContent t={t} />}
            </div>
          </div>
        </div>
      </div>
      <RfqDialog open={rfqOpen} onOpenChange={setRfqOpen} />
    </section>
  );
}

function MissionsContent({ t }) {
  const { lang } = useLang();
  const [missions, setMissions] = useState(t('platform.missionsList'));
  useEffect(() => {
    api.get('/missions', { params: { lang } }).then((r) => { if (r.data?.length) setMissions(r.data); }).catch(() => {});
  }, [lang]);
  return (
    <div>
      <img src={IMAGES.meeting} alt="" className="w-full h-56 object-cover rounded-lg mb-6" />
      <h4 className="text-xl font-bold text-brand-navy mb-4">{t('platform.missionsHeading')}</h4>
      <div className="space-y-3">
        {missions.map((m, i) => (
          <div key={i} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-brand-yellow hover-lift">
            <div className="flex items-center gap-4">
              <MapPin className="w-5 h-5 text-brand-yellow" />
              <div>
                <div className="font-semibold text-brand-navy">{m.city}</div>
                <div className="text-xs text-gray-500">{m.focus}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar className="w-4 h-4" /> {m.date}
            </div>
          </div>
        ))}
      </div>
      <a href="#contact" className="inline-flex items-center gap-2 mt-6 text-brand-navy font-semibold hover:gap-3 transition-all">
        {t('platform.missionsBook')} <ArrowRight className="w-4 h-4" />
      </a>
    </div>
  );
}

function PartnersContent({ t, onOpenRfq }) {
  const rfq = t('platform.rfq');
  return (
    <div>
      <img src={IMAGES.factory} alt="" className="w-full h-56 object-cover rounded-lg mb-6" />
      <h4 className="text-xl font-bold text-brand-navy mb-4">{t('platform.partnersHeading')}</h4>
      <div className="grid sm:grid-cols-2 gap-4">
        {t('platform.partnersSteps').map((s, idx) => (
          <div key={s.n} className={`p-5 rounded-lg border transition ${idx === 0 ? 'bg-brand-navy border-brand-navy text-white shadow-md' : 'bg-gray-50 border-gray-100 hover:border-brand-yellow hover-lift'}`}>
            <div className={`font-bold text-2xl ${idx === 0 ? 'text-brand-yellow' : 'text-brand-yellow'}`}>{s.n}</div>
            <div className={`font-semibold mt-1 ${idx === 0 ? 'text-white' : 'text-brand-navy'}`}>{s.t}</div>
            <div className={`text-sm mt-1 ${idx === 0 ? 'text-white/80' : 'text-gray-600'}`}>{s.d}</div>
            {idx === 0 && (
              <>
                <p className="text-xs text-white/70 mt-4 leading-relaxed">{rfq.openHint}</p>
                <button
                  type="button"
                  onClick={onOpenRfq}
                  data-testid="rfq-open-button-platform"
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-brand-yellow text-brand-navy font-bold text-xs rounded-full uppercase tracking-wider hover:bg-yellow-400 transition"
                >
                  <FileEdit className="w-3.5 h-3.5" /> {rfq.cta}
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function RescuedContent({ t }) {
  const { lang } = useLang();
  const [items, setItems] = useState(RESCUED_PRODUCTS);
  useEffect(() => {
    api.get('/rescued-products', { params: { lang } }).then((r) => { if (r.data?.length) setItems(r.data); }).catch(() => {});
  }, [lang]);
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-brand-yellow" />
        <h4 className="text-xl font-bold text-brand-navy">{t('platform.rescuedHeading')}</h4>
      </div>
      <div className="space-y-3">
        {items.map((p) => (
          <div key={p.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover-lift hover:border-brand-yellow">
            <div>
              <div className="font-semibold text-brand-navy">{p.name}</div>
              <div className="text-xs text-gray-500 mt-1">{p.origin} • {p.quantity}</div>
            </div>
            <div className="text-right">
              <div className="inline-block px-3 py-1 rounded-full bg-brand-yellow/20 text-brand-navy text-sm font-bold">{p.discount}</div>
              <div className="text-xs text-gray-500 mt-1">{p.category}</div>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-5">{t('platform.rescuedNote')}</p>
    </div>
  );
}

function MerchandiseContent({ t }) {
  const cats = t('platform.productCategories');
  return (
    <div>
      <h4 className="text-xl font-bold text-brand-navy mb-2">{t('platform.merchHeading')}</h4>
      <p className="text-sm text-gray-600 mb-6">{t('platform.merchSubtitle')}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {cats.map((cat, i) => (
          <div key={i} className="p-4 border border-gray-200 rounded-lg text-sm font-medium text-brand-navy hover:border-brand-yellow hover:bg-brand-yellow/5 transition-all flex items-center gap-2">
            <span className="text-brand-yellow font-bold text-xs">{String(i + 1).padStart(2, '0')}</span>
            {cat}
          </div>
        ))}
      </div>
    </div>
  );
}

function CompaniesContent({ t }) {
  const { lang } = useLang();
  const [items, setItems] = useState(COMPANIES_OF_MONTH);
  useEffect(() => {
    api.get('/companies-of-month', { params: { lang } }).then((r) => { if (r.data?.length) setItems(r.data); }).catch(() => {});
  }, [lang]);
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Award className="w-5 h-5 text-brand-yellow" />
        <h4 className="text-xl font-bold text-brand-navy">{t('platform.companiesHeading')}</h4>
      </div>
      <div className="space-y-4">
        {items.map((c) => (
          <div key={c.id} className="p-5 border border-gray-200 rounded-lg hover:border-brand-yellow hover-lift">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-bold text-brand-navy text-lg">{c.name}</div>
                <div className="text-xs text-gray-500 mt-1 flex items-center gap-2"><MapPin className="w-3 h-3" /> {c.city} • {t('platform.founded')} {c.founded} • {c.employees}</div>
              </div>
            </div>
            <div className="text-sm text-gray-700">{c.specialty}</div>
            <div className="flex flex-wrap gap-2 mt-3">
              {c.products.map((p, i) => (
                <span key={i} className="text-xs px-2 py-1 bg-gray-100 rounded text-brand-navy">{p}</span>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {c.certifications.map((cert, i) => (
                <span key={i} className="text-[10px] px-2 py-0.5 bg-brand-yellow/20 text-brand-navy rounded font-semibold">{cert}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
